import { useCallback, useMemo } from "react";
import { snakeCase } from "lodash-es";
import { useHeadsUpContext } from "@/components/heads-up/context";
import {
  trackEvent,
  TrackedEvent,
  type HeadsUpAnalyticsContextEventParams,
} from "@/domains/metrics";
import { SmartViewKind } from "@/domains/smart/constants";

export enum HeadsUpItemSource {
  Standard = "standard",
  FindMore = "find_more",
}

export interface HeadsUpCardEventMeta {
  section_kind: string;
  section_title?: string | null;
  section_card_id: string;
  section_position_index: number;
  section_is_find_more: boolean;
  item_kind: string;
  item_id: string;
  item_title?: string | null;
  item_position_index: number;
  item_source: HeadsUpItemSource;
}

interface HeadsUpAnalytics {
  trackHover: (meta: HeadsUpCardEventMeta) => void;
  trackClick: (meta: HeadsUpCardEventMeta) => void;
  trackRefreshClick: () => void;
  trackRegenerateClick: () => void;
  trackFindMoreClick: () => void;
}

export function buildHeadsUpBaseContextPayload({
  contextId,
  contextNoteId,
  contextNoteTitle,
  smartViewId,
  activeSpaceAccountId,
}: {
  contextId?: string | null;
  contextNoteId?: string | null;
  contextNoteTitle?: string | null;
  smartViewId?: string | null;
  activeSpaceAccountId?: string | null;
}): HeadsUpAnalyticsContextEventParams {
  return {
    context_id: contextId ?? undefined,
    context_note_id: contextNoteId ?? undefined,
    context_note_title: contextNoteTitle ?? undefined,
    smart_view_id: smartViewId ?? undefined,
    smart_view_kind: snakeCase(SmartViewKind.HeadsUp),
    active_space_account_id: activeSpaceAccountId ?? undefined,
  };
}

export function useHeadsUpBaseContextPayload(): HeadsUpAnalyticsContextEventParams {
  const model = useHeadsUpContext();

  return useMemo(
    () =>
      buildHeadsUpBaseContextPayload({
        contextId: model.context.contextId,
        contextNoteId: model.context.activeNoteId,
        contextNoteTitle: model.context.activeNoteTitle,
        smartViewId: model.context.smartViewId,
        activeSpaceAccountId: model.context.spaceAccountId,
      }),
    [
      model.context.activeNoteId,
      model.context.activeNoteTitle,
      model.context.contextId,
      model.context.smartViewId,
      model.context.spaceAccountId,
    ]
  );
}

export function useHeadsUpAnalytics(): HeadsUpAnalytics {
  const basePayload = useHeadsUpBaseContextPayload();

  const trackHover = useCallback(
    (meta: HeadsUpCardEventMeta) => {
      trackEvent(TrackedEvent.HeadsUpItemHover, {
        ...basePayload,
        ...meta,
      });
    },
    [basePayload]
  );

  const trackClick = useCallback(
    (meta: HeadsUpCardEventMeta) => {
      trackEvent(TrackedEvent.HeadsUpItemClick, {
        ...basePayload,
        ...meta,
      });
    },
    [basePayload]
  );

  const trackRefreshClick = useCallback(() => {
    trackEvent(TrackedEvent.HeadsUpRefreshClick, { ...basePayload });
  }, [basePayload]);

  const trackRegenerateClick = useCallback(() => {
    trackEvent(TrackedEvent.HeadsUpRegenerateClick, { ...basePayload });
  }, [basePayload]);

  const trackFindMoreClick = useCallback(() => {
    trackEvent(TrackedEvent.HeadsUpFindMoreClick, { ...basePayload });
  }, [basePayload]);

  return {
    trackHover,
    trackClick,
    trackRefreshClick,
    trackRegenerateClick,
    trackFindMoreClick,
  };
}
